/**
 * 快捷绑卡页协议配置
 * HTML 文件放置于 public/agreements/ 目录
 * 运行时优先读取 public/agreements/manifest.json
 */

import bundledManifest from './agreement-manifest.json'

const PROTOCOL_META = {
  '100-1': { title: '中国邮政储蓄银行借记卡快捷支付业务协议' },
  '102-1': { title: '中国工商银行快捷支付业务服务协议' },
  '103-1': { title: '农行快捷授权扣款协议' },
  '104-1': { title: '中国银行股份有限公司借记卡快捷支付协议' },
  '105-1': { title: '中国建设银行非银行支付机构快捷支付协议' },
  '105-2': { title: '中国建设银行银联快捷支付业务客户协议' },
  '105-3': { title: '中国建设银行快捷支付业务个人信息保护协议' },
  '301-1': { title: '交通银行借记卡快捷支付业务服务协议' },
  '302-1': { title: '中信银行借记卡快捷支付线上服务协议' },
  '302-2': { title: '中信用户授权协议' },
  '303-1': { title: '光大快捷挂网协议' },
  '304-1': { title: '华夏银行快捷支付业务服务协议' },
  '305-1': { title: '中国民生银行快捷支付业务用户协议' },
  '306-1': { title: '广发银行快捷支付业务用户服务协议' },
  '307-1': { title: '平安银行快捷支付服务协议' },
  '309-1': { title: '兴业银行快捷支付业务线上服务协议' },
  '310-1': { title: '上海浦东发展银行个人网上快捷支付业务客户服务协议' },
  '401-1': { title: '上海银行快捷支付服务协议' },
  '403': { title: '北京银行快捷支付授权协议' },
  '479-1': { title: '蒙商银行快捷支付业务服务协议' },
  '1413-1': { title: '重庆农商行快捷支付服务协议' },
  '1424-1': { title: '江苏农商联合银行快捷支付业务服务协议' },
  '1529-1': { title: '广东农信快捷支付业务服务协议' },
  'cpcn-1': { title: '中金支付银行卡快捷支付签约' },
  'cpcn-2': { title: '中金支付个人信息保护及隐私' },
  'cpcn-3': { title: '中金支付儿童个人信息保护协议' }
}

/** bank_id -> 协议 id 列表 */
const BANK_PROTOCOL_MAP = {
  '100': ['100-1'],
  '102': ['102-1'],
  '103': ['103-1'],
  '104': ['104-1'],
  '105': ['105-1', '105-2', '105-3'],
  '301': ['301-1'],
  '302': ['302-1', '302-2'],
  '303': ['303-1'],
  '304': ['304-1'],
  '305': ['305-1'],
  '306': ['306-1'],
  '307': ['307-1'],
  '309': ['309-1'],
  '310': ['310-1'],
  '401': ['401-1'],
  '403': ['403'],
  '479': ['479-1'],
  '1413': ['1413-1'],
  '1424': ['1424-1'],
  '1529': ['1529-1']
}

const DEFAULT_PROTOCOL_IDS = ['cpcn-1', 'cpcn-2', 'cpcn-3']

let manifestFiles = (bundledManifest && bundledManifest.files) || {}
let manifestLoaded = false

/** 解析静态资源根路径（兼容 hash 路由与子目录部署） */
export function resolvePublicBaseUrl() {
  if (typeof window !== 'undefined' && window.location) {
    const { origin, pathname } = window.location
    let basePath = pathname || '/'
    if (/\.html?$/i.test(basePath)) {
      basePath = basePath.slice(0, basePath.lastIndexOf('/') + 1)
    } else if (!basePath.endsWith('/')) {
      basePath = `${basePath}/`
    }
    return `${origin}${basePath}`
  }
  const envBase = process.env.BASE_URL || '/'
  if (!envBase) {
    return '/'
  }
  return envBase.endsWith('/') ? envBase : `${envBase}/`
}

export function resolveAgreementFileUrl(fileName) {
  return `${resolvePublicBaseUrl()}agreements/${encodeURIComponent(fileName)}`
}

export function applyAgreementManifest(files) {
  if (files && typeof files === 'object') {
    manifestFiles = files
  }
}

export function getAgreementManifestFiles() {
  return manifestFiles
}

export async function loadAgreementManifestFromServer(force = false) {
  if (manifestLoaded && !force) {
    return manifestFiles
  }
  try {
    const url = `${resolvePublicBaseUrl()}agreements/manifest.json?t=${Date.now()}`
    const res = await fetch(url, { cache: 'no-cache' })
    if (res.ok) {
      const data = await res.json()
      if (data && data.files) {
        applyAgreementManifest(data.files)
      }
    }
  } catch (err) {
    // 使用打包内置 manifest
  }
  manifestLoaded = true
  return manifestFiles
}

function resolveAgreementFileName(id) {
  if (manifestFiles[id]) {
    return manifestFiles[id]
  }
  return `${id}.html`
}

function toProtocolItem(id) {
  const meta = PROTOCOL_META[id] || { title: '快捷支付相关协议' }
  const fileName = resolveAgreementFileName(id)
  return {
    id,
    title: meta.title,
    fileName,
    url: resolveAgreementFileUrl(fileName)
  }
}

/** 根据 bank_id 获取应展示的协议列表 */
export function getProtocolsByBankId(bankId) {
  const normalizedBankId = String(bankId == null ? '' : bankId).trim()
  const ids = BANK_PROTOCOL_MAP[normalizedBankId] || DEFAULT_PROTOCOL_IDS
  return ids.map(toProtocolItem)
}

export function getDefaultProtocols() {
  return DEFAULT_PROTOCOL_IDS.map(toProtocolItem)
}

export function hasAgreementFile(id) {
  return Boolean(manifestFiles[id])
}

export { DEFAULT_PROTOCOL_IDS, BANK_PROTOCOL_MAP, PROTOCOL_META }
